import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import "./VideoPage.css";

const VideoPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const roomId =
    location.state?.roomId ||
    new URLSearchParams(location.search).get("roomId");

  const localVideoRef    = useRef(null);
  const remoteVideoRef   = useRef(null);
  const remoteStreamRef  = useRef(null); // buffers stream if video el not ready yet
  const peerConnectionRef = useRef(null);
  const socketRef        = useRef(null);
  const localStreamRef   = useRef(null);
  const pendingCandidates = useRef([]);

  const [status, setStatus]       = useState("Connecting...");
  const [isMuted, setIsMuted]     = useState(false);
  const [isCamOff, setIsCamOff]   = useState(false);
  const [remoteJoined, setRemoteJoined] = useState(false);

  const ICE_SERVERS = {
    iceServers: [
      { urls: "stun:stun.l.google.com:19302" },
      { urls: "stun:stun1.l.google.com:19302" },
      { urls: "stun:stun2.l.google.com:19302" },
    ],
  };

  const remoteVideoCallbackRef = useCallback((node) => {
    remoteVideoRef.current = node;
    if (node && remoteStreamRef.current) {
      node.srcObject = remoteStreamRef.current;
      node.play().catch(() => {});
    }
  }, []);

  const attachRemoteStream = useCallback((stream) => {
    remoteStreamRef.current = stream;
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = stream;
      remoteVideoRef.current.play().catch(() => {});
    }
    setRemoteJoined(true);
    setStatus("Connected ✓");
  }, []);

  const createPeerConnection = useCallback(() => {
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
    }

    const pc = new RTCPeerConnection(ICE_SERVERS);
    peerConnectionRef.current = pc;

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => {
        pc.addTrack(track, localStreamRef.current);
      });
    }

    pc.ontrack = (event) => {
      console.log("✅ ontrack fired | tracks:", event.track.kind);
      let stream;
      if (event.streams && event.streams[0]) {
        stream = event.streams[0];
      } else {
        stream = new MediaStream();
        stream.addTrack(event.track);
      }
      attachRemoteStream(stream);
    };

    pc.onicecandidate = (event) => {
      if (event.candidate && socketRef.current) {
        socketRef.current.emit("ice-candidate", {
          roomId,
          candidate: event.candidate,
        });
      }
    };

    pc.onconnectionstatechange = () => {
      console.log("🔗 Connection state:", pc.connectionState);
      if (pc.connectionState === "connected") {
        setStatus("Connected ✓");
      }
      if (pc.connectionState === "disconnected" || pc.connectionState === "failed") {
        setStatus("Peer disconnected");
        setRemoteJoined(false);
        remoteStreamRef.current = null;
        if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
      }
    };

    return pc;
  }, [roomId, attachRemoteStream]);

  const flushCandidates = async (pc) => {
    for (const c of pendingCandidates.current) {
      await pc.addIceCandidate(new RTCIceCandidate(c)).catch(console.warn);
    }
    pendingCandidates.current = [];
  };

  const createOffer = useCallback(async () => {
    const pc = createPeerConnection();
    const offer = await pc.createOffer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: true,
    });
    await pc.setLocalDescription(offer);
    socketRef.current.emit("offer", { roomId, offer });
    console.log("📤 Offer sent");
  }, [createPeerConnection, roomId]);

  const createAnswer = useCallback(async (offer) => {
    const pc = createPeerConnection();
    await pc.setRemoteDescription(new RTCSessionDescription(offer));
    await flushCandidates(pc);
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    socketRef.current.emit("answer", { roomId, answer });
    console.log("📤 Answer sent");
  }, [createPeerConnection, roomId]);

  useEffect(() => {
    if (!roomId) {
      navigate("/video");
      return;
    }

    // ✅ REPLACE LOCALHOST with deployed backend URL
    const socket = io("https://whatsappbackend-sq5p.onrender.com", {
      transports: ["websocket", "polling"],
    });
    socketRef.current = socket;

    const startMedia = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
        localStreamRef.current = stream;
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }
        socket.emit("joinRoom", roomId);
        setStatus("Waiting for peer...");
        console.log("🎥 Local media ready, joined room:", roomId);
      } catch (err) {
        setStatus("❌ Camera/mic denied — check browser permissions");
        console.error(err);
      }
    };

    startMedia();

    socket.on("roomReady", async () => {
      console.log("📣 roomReady — I am the caller");
      setStatus("Peer joined! Connecting...");
      await createOffer();
    });

    socket.on("offer", async ({ offer }) => {
      console.log("📥 Offer received");
      setStatus("Connecting...");
      await createAnswer(offer);
    });

    socket.on("answer", async ({ answer }) => {
      console.log("📥 Answer received");
      const pc = peerConnectionRef.current;
      if (pc && pc.signalingState !== "stable") {
        await pc.setRemoteDescription(new RTCSessionDescription(answer));
        await flushCandidates(pc);
      }
    });

    socket.on("ice-candidate", async ({ candidate }) => {
      const pc = peerConnectionRef.current;
      if (pc && pc.remoteDescription && pc.remoteDescription.type) {
        await pc.addIceCandidate(new RTCIceCandidate(candidate)).catch(console.warn);
      } else {
        pendingCandidates.current.push(candidate);
      }
    });

    socket.on("peerLeft", () => {
      console.log("👋 Peer left");
      setStatus("Peer disconnected");
      setRemoteJoined(false);
      remoteStreamRef.current = null;
      if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
    });

    return () => {
      localStreamRef.current?.getTracks().forEach((t) => t.stop());
      peerConnectionRef.current?.close();
      socket.disconnect();
    };
  }, [roomId, navigate, createOffer, createAnswer]);

  const toggleMute = () => {
    localStreamRef.current?.getAudioTracks().forEach((t) => {
      t.enabled = !t.enabled;
    });
    setIsMuted((prev) => !prev);
  };

  const toggleCam = () => {
    localStreamRef.current?.getVideoTracks().forEach((t) => {
      t.enabled = !t.enabled;
    });
    setIsCamOff((prev) => !prev);
  };

  const endCall = () => {
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    peerConnectionRef.current?.close();
    socketRef.current?.emit("leaveRoom", roomId);
    socketRef.current?.disconnect();
    navigate("/video");
  };

  return (
    <div className="vp-container">
      <div className="vp-header">
        <div className="vp-room-badge">
          <span className={`vp-dot ${status.includes("✓") ? "green" : ""}`} />
          Room: <strong>{roomId?.slice(0, 8)}…</strong>
        </div>
        <div className={`vp-status ${status.includes("✓") ? "connected" : ""}`}>
          {status}
        </div>
      </div>

      <div className={`vp-stage ${remoteJoined ? "dual" : "solo"}`}>
        <div className="vp-video-wrap remote">
          <video
            ref={remoteVideoCallbackRef}
            autoPlay
            playsInline
            className="vp-video"
          />
          {!remoteJoined && (
            <div className="vp-placeholder">
              <div className="vp-avatar">👤</div>
              <p>Waiting for peer to join…</p>
            </div>
          )}
          <span className="vp-label">Remote</span>
        </div>

        <div className="vp-video-wrap local">
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className="vp-video"
          />
          {isCamOff && (
            <div className="vp-cam-off">
              <span>📷</span>
              <p>Camera off</p>
            </div>
          )}
          <span className="vp-label">You</span>
        </div>
      </div>

      <div className="vp-controls">
        <button className={`vp-btn ${isMuted ? "active" : ""}`} onClick={toggleMute}>
          {isMuted ? "🔇" : "🎙️"}
          <span>{isMuted ? "Unmute" : "Mute"}</span>
        </button>

        <button className={`vp-btn ${isCamOff ? "active" : ""}`} onClick={toggleCam}>
          {isCamOff ? "📷" : "📹"}
          <span>{isCamOff ? "Cam On" : "Cam Off"}</span>
        </button>

        <button className="vp-btn end" onClick={endCall}>
          📵
          <span>End Call</span>
        </button>
      </div>
    </div>
  );
};

export default VideoPage;